<template>
  <div class="page">
    <div v-if="!showConfermaSocialMedia" class="input">
      <div class="title">
        <h3>
          Ciao! Benvenuto nel percorso di valutazione e posizionamento del tuo brand.
          Il percorso è composto da 4 passi.
          Cominciamo con il primo passo che serve a conoscere la presenza online del tuo brand!
        </h3>
      </div>
      <div class="form-container">
        <div class="form-group">
          <label for="company_name" class="input-text">Inserisci il nome della tua azienda:</label>
          <input type="text" id="company_name" v-model="company_name" @input="fetchCompanySuggestions"
            @focus="showSuggestions = true" @blur="hideSuggestions" class="bar" />
          <ul v-if="showSuggestions" class="suggestions-list">
            <li v-for="suggestion in suggestions" :key="suggestion.company_id" @click="selectSuggestion(suggestion)">
              {{ suggestion.company_name }}
            </li>
          </ul>
        </div>
        <div class="form-group">
          <label for="sector" class="input-text">Inserisci il settore della tua azienda:</label>
          <input type="text" id="sector" v-model="sector" class="bar" />
        </div>
        <div class="form-group">
          <label for="website" class="input-text">Inserisci l'URL del tuo sito Web:</label>
          <input type="text" id="website" v-model="website" class="bar" />
        </div>
        <div class="checkbox">
          <input type="checkbox" id="noWebsite" v-model="noWebsite" />
          <label for="noWebsite">Clicca qui se non hai un sito web</label>
        </div>
        <div class="invia-button">
          <ButtonTemplate :button-text="'Invia'" @click="submitForm" />
        </div>
      </div>
    </div>

    <div v-if="showConfermaSocialMedia" class="social">
      <ConfermaSocialMedia />
    </div>

    <!-- Custom Modal for confirming session continuation -->
    <div v-if="showPopup" class="custom-modal-overlay" @click="closeModal">
      <div class="custom-modal" @click.stop>
        <div class="custom-modal-header">
          <h3>Sessione Esistente</h3>
        </div>
        <div class="custom-modal-body">
          Abbiamo trovato un sessione già iniziata da te per la stessa azienda il {{ existingActiveSession.date }}. Vuoi
          continuare o iniziarne una nuova?
        </div>
        <div class="custom-modal-footer">
          <button @click="continueExistingActiveSession(existingActiveSession)" class="btn-success">Continua quella
            esistente</button>
          <button @click="cancelAndStartNewSession(existingActiveSession, this.userId)" class="btn-danger">Inizia una
            nuova</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabaseClient';
import { useAuthStore } from '@/stores/authStore';
import { useSessionStore } from '@/stores/sessionStore';
import ButtonTemplate from '../../widgets/button.vue';
import ConfermaSocialMedia from './ConfermaSocialMedia.vue';

export default {
  setup() {
    const sessionstore = useSessionStore();
    return { sessionstore };
  },

  name: 'StartInputBar',
  components: {
    ButtonTemplate,
    ConfermaSocialMedia
  },
  data() {
    return {
      company_name: '',
      company_id: '',
      sector: '',
      website: '',
      noWebsite: false,
      showConfermaSocialMedia: false,
      showSuggestions: false,
      suggestions: [],
      existingActiveSession: { date: null },
      showPopup: false,
      userId: ''
    };
  },
  methods: {
    async fetchCompanySuggestions() {
      if (this.company_name.length > 1) {
        const { data, error } = await supabase
          .from('companies')
          .select('company_id, company_name, sector, website')
          .ilike('company_name', `%${this.company_name}%`);

        if (error) {
          console.error('Error fetching company suggestions:', error.message);
          return;
        }

        this.suggestions = data;
        this.showSuggestions = true;
      } else {
        this.suggestions = [];
      }
    },

    async selectSuggestion(suggestion) {
      this.company_name = suggestion.company_name;
      this.sector = suggestion.sector;
      this.website = suggestion.website;
      this.showSuggestions = false;
    },

    checkFieldsCompleteness() {
      if (!this.noWebsite && (!this.company_name || !this.website)) {
        alert('Compila entrambi i campi prima di procedere.');
        return false;
      } else if (this.noWebsite && !this.company_name) {
        alert('Compila il campo del nome dell\'azienda prima di procedere.');
        return false;
      }
    },

    checkAuth() {
      const authStore = useAuthStore();
      const user = authStore.user;
      this.userId = user ? user.id : null;

      if (!this.userId) {
        console.error('User not authenticated');
        return false;
      }
    },

    async submitForm() {
      if (this.checkFieldsCompleteness() === false) {
        return;
      }
      if (this.checkAuth() === false) {
        return;
      }

      // check if company already exists in database
      const { data: existingCompany, error: fetchError } = await supabase
        .from('companies')
        .select('company_id')
        .eq('company_name', this.company_name)
        .limit(1)
        .maybeSingle();
      if (fetchError) {
        console.error('Error checking existing company:', fetchError.message);
        return;
      }

      // if company does not exist, insert it
      if (!existingCompany) {
        const { data: newCompany, error: insertError } = await supabase
          .from('companies')
          .insert([{ company_name: this.company_name, sector: this.sector, website: this.website }])
          .select('company_id')
          .single();
        if (insertError) {
          console.error('Error inserting new company:', insertError.message);
          return;
        }
        this.company_id = newCompany.company_id;
      } else {

        // if company already exists, use its id fetched from the database
        this.company_id = existingCompany.company_id;
      }

      const { data: existingActiveSession, error: sessionFetchError } = await supabase
        .from('sessions')
        .select('session_id, date, status')
        .eq('user_id', this.userId)
        .eq('company_id', this.company_id)
        .eq('status', 'active')
        .maybeSingle();

      if (sessionFetchError) {
        console.error('Error checking existing session:', sessionFetchError.message);
        return;
      }

      // if an active session already exists, show a popup to ask the user if they want to continue it
      if (existingActiveSession) {
        this.existingActiveSession = existingActiveSession;
        this.showPopup = true;
      } else {
        this.startNewSession(this.userId);
      }
    },


    /**
     * Starts a new session for the user
     * 
     * This method:
     * 1. Generates a random session ID
     * 2. Inserts a new session record into the database
     * 3. Initializes the session store with the new session data
     * 4. Shows the ConfermaSocialMedia component
     * 
     * @param {string} userId - The user's ID
     * @returns {Promise<void>}
     */
    async startNewSession(userId) {
      const session_id = Math.random().toString(36).substring(2, 27);
      const timestamp = new Date().toISOString();

      const { error: sessionError } = await supabase
        .from('sessions')
        .insert([{
          session_id: session_id,
          date: timestamp,
          company_id: this.company_id,
          user_id: userId,
          status: 'active'
        }]);

      if (sessionError) {
        console.error('Error inserting new session:', sessionError.message);
        return;
      }

      // save session attributes to pinia store
      this.sessionstore.init(
        session_id,
        timestamp,
        this.company_name,
        this.company_id,
        this.website
      );

      this.showConfermaSocialMedia = true;
    },

    /**
     * Handles the continuation of an existing active session
     * 
     * This method:
     * 1. Initializes the session store with the existing active session data
     * 2. Closes the popup and shows the ConfermaSocialMedia component
     * 
     * @param {Object} existingActiveSession - The existing active session object
     * @returns {Promise<void>}
     */

    async continueExistingActiveSession(existingActiveSession) {
      // simply save 
      this.sessionstore.init(
        existingActiveSession.session_id,
        existingActiveSession.date,
        this.company_name,
        this.company_id,
        this.website
      );
      this.showPopup = false;
      this.showConfermaSocialMedia = true;
    },

    /**
     * Handles the cancellation of an existing active session and starts a new one
     * 
     * This method:
     * 1. Updates the session status to 'cancelled'
     * 2. Logs the session id
     * 3. Starts a new session
     * 
     * @param {Object} existingActiveSession - The existing active session object
     * @param {string} userId - The user's ID
     * @returns {Promise<void>}
     */

    async cancelAndStartNewSession(existingActiveSession, userId) {
      const { error: updateError } = await supabase
        .from('sessions')
        .update({ status: 'cancelled' })
        .eq('session_id', existingActiveSession.session_id);

      console.log("session id:", existingActiveSession)

      if (updateError) {
        console.error('Error updating session status:', updateError.message);
        return;
      }

      this.showPopup = false;
      this.startNewSession(userId);
    },
    closeModal() {
      this.showPopup = false;
    },
  }
};
</script>

<style scoped>
html,
body {
  height: 100%;
  margin: 0;
}

.social {
  position: relative;
  background: transparent;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.page {
  position: relative;
  background: linear-gradient(135deg, #ffffff, #56BCEE);
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 100vh;
  padding: 2rem;
  width: 100%;
}

.input {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.title {
  margin-bottom: 2rem;
  max-width: 800px;
  color: #1a1a1a;
  font-weight: 600;
  font-size: 1.5rem;
  text-align: center;
  line-height: 1.4;
  width: 100%;
}

.form-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 700px;
  padding: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
  width: 100%;
  align-items: center;
  text-align: center;
  padding: 0;
  position: relative;
}

.input-text {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  color: #1a1a1a;
  font-weight: 500;
  text-align: center;
  width: 100%;
  padding: 0;
}

.bar {
  width: 100%;
  height: 3rem;
  padding: 0;
  border: 2px solid #2e4eed;
  background: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  transition: all 0.3s ease;
  text-align: center;
}

.bar:focus {
  outline: none;
  border-color: #56BCEE;
  box-shadow: 0 0 0 2px rgba(86, 188, 238, 0.2);
}

.checkbox {
  margin-bottom: 1rem;
  color: #1a1a1a;
  font-style: normal;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
  width: 100%;
  padding: 0;
}

.checkbox input[type="checkbox"] {
  width: 1.2rem;
  height: 1.2rem;
  border: 2px solid #2e4eed;
  cursor: pointer;
  margin: 0;
}

.suggestions-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  border: 2px solid #2e4eed;
  background-color: #fff;
  position: absolute;
  width: 100%;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(46, 78, 237, 0.1);
  top: calc(100% + 0.5rem);
  left: 0;
}

.suggestions-list li {
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  text-align: center;
  width: 100%;
}

.suggestions-list li:hover {
  background-color: rgba(46, 78, 237, 0.05);
}

.invia-button {
  --button-border-color: #2e4eed;
  --button-text-color: #ffffff;
  --button-background-color: #2e4eed;
  --button-background-hover-color: #1e3ed8;
  --button-border-radius: 0;
  margin-top: 1rem;
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Custom Modal Styles */
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.custom-modal {
  background: white;
  padding: 2rem;
  border-radius: 0;
  width: 500px;
  max-width: 90%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.custom-modal-header {
  margin-bottom: 1.5rem;
}

.custom-modal-header h3 {
  color: #1a1a1a;
  font-size: 1.25rem;
  font-weight: 600;
}

.custom-modal-body {
  margin-bottom: 1.5rem;
  color: #1a1a1a;
  line-height: 1.5;
}

.custom-modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.btn-success {
  background-color: #2e4eed;
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
}

.btn-success:hover {
  background-color: #1e3ed8;
}

.btn-danger {
  background-color: #ffffff;
  border: 2px solid #2e4eed;
  color: #2e4eed;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-danger:hover {
  background-color: rgba(46, 78, 237, 0.05);
}

@media (max-width: 768px) {
  .page {
    padding: 1rem;
  }

  .title {
    font-size: 1.25rem;
    margin: 1rem;
  }

  .form-container {
    width: 100%;
  }

  .bar {
    height: 2.5rem;
  }

  .custom-modal {
    width: 90%;
    padding: 1.5rem;
  }

  .custom-modal-footer {
    flex-direction: column;
  }

  .btn-success,
  .btn-danger {
    width: 100%;
  }
}
</style>
